import { LocationMatrixHelper, filterClasses } from "@swivl-mono/helpers";
import { LocationScope, LocationsMatrix } from "@swivl-mono/types";
import { OldPanel, Spinner, Switch } from "@swivl-mono/ui";
import React, { useEffect, useRef, useState } from "react";

import LocationsModel from "../../Models/Locations/Locations.Model";

/**
 * Reusable "all vs. group vs. specific locations" picker driven by a raw
 * `LocationsMatrix` (org / groups / locations), decoupled from
 * `MemberPermission`. The analytics `LocationPicker` wraps this for its
 * permission use case; Document Ingest uses it directly.
 */

interface LocationMatrixPickerOptionProps {
  on: boolean;
  label: React.ReactNode;
  indent: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

function LocationMatrixPickerOption({ on, label, indent, onChange }: LocationMatrixPickerOptionProps): JSX.Element {
  return (
    <div className="mb-3 flex w-full flex-row">
      <div className={filterClasses("flex-1", indent)}>{label}</div>
      <div className="col flex">
        <Switch on={on} onChange={onChange} />
      </div>
    </div>
  );
}

interface LocationMatrixPickerProps {
  matrix: LocationsMatrix;
  orgId: number;
  onChange: (matrix: LocationsMatrix) => void;
}

export const LocationMatrixPicker: React.FC<LocationMatrixPickerProps> = ({ matrix, orgId, onChange }) => {
  const { groups } = LocationsModel.useState();
  const [isOpen, setIsOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  const orgWide = matrix.orgId === orgId;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  let message = "";
  if (orgWide) {
    message = "All locations";
  } else if (groups) {
    let locationsEnabled = 0;
    groups.forEach((group) => {
      if (matrix.groupIds?.includes(group.id)) {
        locationsEnabled += group.locations.length;
      } else if (group.locations) {
        locationsEnabled += group.locations.filter((location) => matrix.locationIds?.includes(location.id)).length;
      }
    });
    message = locationsEnabled > 0 ? `Filtered by ${locationsEnabled} ${locationsEnabled === 1 ? "location" : "locations"}` : "No locations selected";
  } else {
    message = "No locations selected";
  }

  if (!groups) {
    return <OldPanel style={OldPanel.Style.GREY} header={<Spinner />} />;
  }

  return (
    <div ref={panelRef}>
      <OldPanel style={OldPanel.Style.GREY} header={message} open={isOpen} onToggle={() => setIsOpen(!isOpen)}>
        <div className="flex flex-col">
          <LocationMatrixPickerOption
            key="all"
            on={orgWide}
            label="All"
            indent=""
            onChange={(event) => onChange(LocationMatrixHelper.updateMatrix(event.target.checked, LocationScope.ORG, orgId, matrix, groups))}
          />
          {groups.map((group) => {
            const groupWide = matrix.groupIds?.includes(group.id) ?? false;

            return (
              <div key={"container-" + group.id}>
                <LocationMatrixPickerOption
                  on={orgWide || groupWide}
                  label={group.name}
                  key={"group-" + group.id}
                  indent="pl-4"
                  onChange={(event) =>
                    onChange(LocationMatrixHelper.updateMatrix(event.target.checked, LocationScope.GROUP, group.id, matrix, groups))
                  }
                />
                {group.locations.map((location) => {
                  const locationWide = matrix.locationIds?.includes(location.id) ? true : false;

                  return (
                    <LocationMatrixPickerOption
                      on={orgWide || groupWide || locationWide}
                      label={<>{location.name}</>}
                      indent="pl-8"
                      key={"location-" + location.id}
                      onChange={(event) =>
                        onChange(LocationMatrixHelper.updateMatrix(event.target.checked, LocationScope.LOCATION, location.id, matrix, groups))
                      }
                    />
                  );
                })}
              </div>
            );
          })}
        </div>
      </OldPanel>
    </div>
  );
};
